<template>
  <v-card class="" style="border-radius: 12px">
    <div class="d-flex align-center mb-4">
      <v-icon class="mr-2" color="info" size="small">mdi-compare-horizontal</v-icon>
      <span style="font-size: 16px; font-weight: 700">Year-over-Year Comparison</span>
    </div>

    <div class="spec-table-wrapper">
      <v-table density="comfortable" class="spec-table">
        <thead>
          <tr>
            <th class="text-left spec-year-header sticky-col sortable-header" @click="toggleSort('__year__')">
              <div class="d-flex align-center ga-1">
                <span>Year</span>
                <v-icon v-if="sortColumn === '__year__'" size="small">{{ sortIcon }}</v-icon>
                <v-icon v-else size="small" class="sort-placeholder">mdi-arrow-up</v-icon>
              </div>
            </th>
            <th v-for="row in comparisonRows" :key="row.label" class="text-center spec-metric-header sortable-header"
                @click="toggleSort(row.label)">
              <div class="d-flex flex-row align-center ga-1">
                <v-icon :color="row.iconColor" size="18">{{ row.icon }}</v-icon>
                <span class="metric-label">{{ row.label }}</span>
                <v-icon v-if="sortColumn === row.label" size="small">{{ sortIcon }}</v-icon>
                <v-icon v-else size="small" class="sort-placeholder">mdi-arrow-up</v-icon>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="{ year, idx } in sortedYearEntries" :key="year" :class="{ 'best-year-row': idx === bestYearIdx }">
            <td class="sticky-col year-cell">
              <div class="d-flex align-center">
                <span class="font-weight-bold text-body-1">{{ year }}</span>
                <v-chip v-if="idx === bestYearIdx" color="success" size="x-small" label variant="tonal" class="ml-2">
                  <v-icon size="x-small" class="mr-1">mdi-trophy</v-icon>Best
                </v-chip>
              </div>
            </td>
            <td v-for="row in comparisonRows" :key="row.label" class="text-center value-cell">
              <div class="d-flex flex-row align-center ga-2">
                <span class="text-body-1 font-weight-bold" :class="row.bestIdx === idx ? 'text-success' : ''">
                  {{ formatNumber(row.values[idx]) }}
                </span>
                <v-chip v-if="idx > 0 && row.diffs[idx]" :color="row.diffs[idx].color" size="x-small" label variant="tonal">
                  <v-icon size="x-small" class="mr-1">{{ row.diffs[idx].icon }}</v-icon>
                  {{ row.diffs[idx].text }}
                </v-chip>
              </div>
            </td>
          </tr>
        </tbody>
      </v-table>
    </div>

    <!-- Summary -->
    <div class="d-flex align-center justify-center mt-4 pa-3 rounded" style="background-color: #f5f5f5">
      <v-icon color="success" class="mr-2">mdi-trophy</v-icon>
      <span class="text-body-1 font-weight-medium">{{ verdictText }}</span>
    </div>
  </v-card>
</template>

<script>
export default {
  name: 'YearlyComparisonTable',
  props: {
    years: {
      type: Array,
      required: true,
    },
    comparisonRows: {
      type: Array,
      required: true,
    },
    bestYearIdx: {
      type: Number,
      required: true,
    },
    verdictText: {
      type: String,
      required: true,
    },
    formatNumber: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      sortColumn: null,
      sortDirection: null,
    };
  },
  computed: {
    sortedYearEntries() {
      const entries = this.years.map((year, idx) => ({ year, idx }));
      if (!this.sortColumn || !this.sortDirection) return entries;
      const colIdx = this.comparisonRows.findIndex((r) => r.label === this.sortColumn);
      entries.sort((a, b) => {
        let valA, valB;
        if (colIdx >= 0) {
          valA = this.comparisonRows[colIdx].values[a.idx];
          valB = this.comparisonRows[colIdx].values[b.idx];
        } else {
          valA = a.year;
          valB = b.year;
        }
        if (valA == null) return 1;
        if (valB == null) return -1;
        if (valA < valB) return this.sortDirection === 'asc' ? -1 : 1;
        if (valA > valB) return this.sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
      return entries;
    },
    sortIcon() {
      return this.sortDirection === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down';
    },
  },
  methods: {
    toggleSort(column) {
      if (this.sortColumn !== column) {
        this.sortColumn = column;
        this.sortDirection = 'asc';
      } else if (this.sortDirection === 'asc') {
        this.sortDirection = 'desc';
      } else {
        this.sortColumn = null;
        this.sortDirection = null;
      }
    },
  },
};
</script>

<style scoped>
.spec-table-wrapper {
  overflow-x: auto;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
}

.spec-table {
  border-radius: 0;
  min-width: 100%;
}

.spec-table thead tr {
  background: linear-gradient(180deg, #fafafa 0%, #f0f0f0 100%);
}

.spec-table thead tr th {
  font-size: 0.8rem;
  font-weight: 700;
  padding: 16px 20px;
  border-bottom: 2px solid #e0e0e0;
  white-space: nowrap;
}

.spec-table tbody tr td {
  padding: 18px 20px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
  white-space: nowrap;
}

.spec-table tbody tr:last-child td {
  border-bottom: none;
}

.spec-table tbody tr:nth-child(odd) {
  background-color: rgba(0, 0, 0, 0.015);
}

.spec-table tbody tr:hover {
  background-color: rgba(118, 35, 126, 0.03) !important;
}

.best-year-row {
  background-color: rgba(76, 175, 80, 0.04) !important;
}

.spec-year-header {
  width: 140px;
  min-width: 140px;
}

.spec-metric-header {
  min-width: 160px;
}

.metric-label {
  font-size: 0.75rem;
  line-height: 1.2;
  text-align: center;
}

.sortable-header {
  cursor: pointer;
  user-select: none;
}

.sortable-header:hover {
  background-color: rgba(118, 35, 126, 0.04);
}

.sort-placeholder {
  visibility: hidden;
}

.year-cell {
  border-right: 2px solid #e0e0e0;
}

.value-cell {
  border-right: 1px solid #f5f5f5;
}

.value-cell:last-child {
  border-right: none;
}

.sticky-col {
  position: sticky;
  left: 0;
  background-color: white;
  z-index: 1;
}

thead .sticky-col {
  background: linear-gradient(180deg, #fafafa 0%, #f0f0f0 100%);
  z-index: 2;
}

.best-year-row .sticky-col {
  background-color: rgba(76, 175, 80, 0.04);
}
</style>
